import { defineComponent as _defineComponent } from 'vue'
import { AdABGroups } from '@/views/Automation/CampaignCreatorV2//validation/Ad';
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcError from '@/views/Automation/CampaignCreatorV2/components/CcError/CcError.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcMediaImage from '@/views/Automation/CampaignCreatorV2/components/CcMediaImage/CcMediaImage.ts.vue';
import CcActionsPopover from '@/views/Automation/CampaignCreatorV2/components/CcPopover/CcActionsPopover.ts.vue';
import {
  AddMediaDropdownEvent,
  ViewLevelActionsEvent,
} from '@/views/Automation/CampaignCreatorV2/components/CcPopover/helpers';
import CcSwitchLarge from '@/views/Automation/CampaignCreatorV2/components/CcSwitch/CcSwitchLarge.ts.vue';
import CcVariableTags from '@/views/Automation/CampaignCreatorV2/components/CcVariableTags/CcVariableTags.ts.vue';
import CcAddImageModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/CcAddImageModal.ts.vue';
import {
  CallToActionType,
  ImageCreative,
  ReadableNameCallToAction,
} from '@/views/Automation/CampaignCreatorV2/helpers/facebook/AdCreative';
import useCreativeStore from '@/views/Automation/CampaignCreatorV2/store/Creative';
import { InternalABVariable, SupportedEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { CardLayer } from '@/views/Automation/CampaignCreatorV2/types';
import Treeselect from '@riophae/vue-treeselect';
import { Position } from '@sh/types';
import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import uid from 'uid';
import { computed, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AdCreative.ts',
  setup(__props) {

const addImageModal = ref<typeof CcAddImageModal | null>(null);

const creativeStore = useCreativeStore();
const creativeGetters = storeToRefs(creativeStore);

const callToActionOptions = computed(() => {
  const action = Object.entries(ReadableNameCallToAction).map(([key, value]) => ({ id: key, label: value }));
  return action;
});

const primaryTexts = computed(() => {
  const texts = creativeGetters.getField.value('title')?.value;
  if (texts?.length > 1) {
    throw new Error('Only one primary text variable is allowed');
  }
  return texts?.[0] || {};
});

const bodies = computed(() => {
  const texts = creativeGetters.getField.value('body')?.value;
  if (texts?.length > 1) {
    throw new Error('Only one primary text variable is allowed');
  }
  return texts?.[0] || {};
});

const headlines = computed(() => {
  const texts = creativeGetters.getField.value('headline')?.value;
  if (texts?.length > 1) {
    throw new Error('Only one primary text variable is allowed');
  }
  return texts?.[0] || {};
});

const callToAction = computed(() => {
  const texts = creativeGetters.getField.value('call_to_action_type')?.value || [];
  return texts;
});

const images = computed(() => {
  const data = creativeGetters.getField.value('image')?.value;
  return data || [];
});

function updatePrimaryText(value: string, variableId: string, index: number) {
  const { value: currentState } =
    (creativeStore.getField('title')?.value || []).find((v) => v.variableId === variableId) || {};
  if (!currentState) throw new Error('No variable found when updating variable');
  currentState[index] = value;
  creativeStore.addOrEditVariable('title', { field: 'title', value: currentState, variableId });
}

function addNewPrimaryText(existingVariableId?: string) {
  if (existingVariableId) {
    const existingValues = primaryTexts.value.value;
    creativeStore.addOrEditVariable('title', {
      field: 'title',
      value: [...existingValues, 'New Text'],
      variableId: existingVariableId,
    });
    return;
  }
  const variableId = uid();
  creativeStore.addOrEditVariable('title', { field: 'title', value: ['New Text'], variableId });
}

function updateBody(value: string, variableId: string, index: number) {
  const { value: currentState } =
    (creativeStore.getField('body')?.value || []).find((v) => v.variableId === variableId) || {};
  if (!currentState) throw new Error('No variable found when updating variable');
  currentState[index] = value;
  creativeStore.addOrEditVariable('body', { field: 'body', value: currentState, variableId });
}

function addNewBody(existingVariableId?: string) {
  if (existingVariableId) {
    const existingValues = bodies.value.value;
    creativeStore.addOrEditVariable('body', {
      field: 'body',
      value: [...existingValues, 'New Body'],
      variableId: existingVariableId,
    });
    return;
  }
  const variableId = uid();
  creativeStore.addOrEditVariable('body', { field: 'body', value: ['New Body'], variableId });
}

function addNewHeadline(existingVariableId?: string) {
  if (existingVariableId) {
    const existingValues = headlines.value.value;
    creativeStore.addOrEditVariable('headline', {
      field: 'headline',
      value: [...existingValues, 'New Headline'],
      variableId: existingVariableId,
    });
    return;
  }
  const variableId = uid();
  creativeStore.addOrEditVariable('headline', { field: 'headline', value: ['New Headline'], variableId });
}

function updateHeadline(value: string, variableId: string, index: number) {
  const { value: currentState } =
    (creativeStore.getField('headline')?.value || []).find((v) => v.variableId === variableId) || {};
  if (!currentState) throw new Error('No variable found when updating variable');
  currentState[index] = value;
  creativeStore.addOrEditVariable('headline', { field: 'headline', value: currentState, variableId });
}

function addNewCallToAction() {
  const variableId = uid();
  creativeStore.addOrEditVariable('call_to_action_type', {
    field: 'call_to_action_type',
    value: CallToActionType.LEARN_MORE,
    variableId,
  });
}

function onRemoveVariable(field: 'title' | 'headline' | 'call_to_action_type' | 'body', variableId: string) {
  creativeStore.removeVariable(field, variableId);
}

function onRemoveItemFromArrayVariable(field: 'title' | 'headline' | 'body', variableId: string, index: number) {
  const { value: currentState } =
    (creativeStore.getField(field)?.value || []).find((v) => v.variableId === variableId) || {};
  if (!currentState) throw new Error('No variable found when updating variable');
  currentState.splice(index, 1);
  creativeStore.addOrEditVariable(field, { field, value: currentState, variableId });
}

function updateCallToAction(value: { id: CallToActionType }, variableId: string) {
  creativeStore.addOrEditVariable('call_to_action_type', { field: 'call_to_action_type', value: value.id, variableId });
}

function onAddMediaEvent(event: ViewLevelActionsEvent) {
  switch (event) {
    case AddMediaDropdownEvent.ADD_IMAGE:
      addImageModal.value?.open();
      break;
    case AddMediaDropdownEvent.ADD_VIDEO:
      break;
  }
}

function onAddImage(images: ImageCreative[]) {
  images.forEach((image, index) => {
    creativeStore.addOrEditVariable('image', {
      field: 'image',
      value: image,
      variableId: image.id,
      name: `New Image ${index}`,
    });
  });
}

function onImageRemove(variableId: string) {
  creativeStore.removeVariable('image', variableId);
}

function onImageEdit(index: number) {
  addImageModal.value?.open(cloneDeep([images.value[index].value]));
}

function onNewGroupAdd() {
  addNewBody();
  addNewHeadline();
  addNewPrimaryText();
  addNewCallToAction();
}

function onImageVariableRename(variable: InternalABVariable<any>, newName: string) {
  creativeStore.addOrEditVariableName('image', variable.abGroup!, variable.variableId, newName);
}

return { __sfc: true,addImageModal, creativeStore, creativeGetters, callToActionOptions, primaryTexts, bodies, headlines, callToAction, images, updatePrimaryText, addNewPrimaryText, updateBody, addNewBody, addNewHeadline, updateHeadline, addNewCallToAction, onRemoveVariable, onRemoveItemFromArrayVariable, updateCallToAction, onAddMediaEvent, onAddImage, onImageRemove, onImageEdit, onNewGroupAdd, onImageVariableRename, AdABGroups, CcCard, CcCardItem, CcError, CcIcon, CcMediaImage, CcActionsPopover, AddMediaDropdownEvent, CcSwitchLarge, CcVariableTags, CcAddImageModal, SupportedEntities, CardLayer, Treeselect, Position }
}

})